/**
 * Legal page content.
 *
 * PROVENANCE, READ BEFORE EDITING. CLAUDE.md hard rule 2 and spec section 19
 * direct the build agent never to write this copy, and from the first commit
 * until August 16, 2026 these pages were a loud placeholder. On that date the
 * owner explicitly instructed the agent to draft this copy and set it live
 * ("you create the first draft and get them set"), overriding his own rule.
 * The draft below is AI-written at the owner's direction, has not been
 * reviewed by a lawyer, and is pending the owner's word-for-word review.
 *
 * Rules that still stand:
 * - No agent edits this text again without equally explicit owner direction.
 * - The Founding Provider section renders the strings from
 *   `src/shared/content/foundingOffer.ts` verbatim. To change what the
 *   program promises, change that module and let the terms, the provider
 *   page, and the confirmation email all follow it.
 * - Site copy must never promise more than this page says (hard rule 6).
 *
 * The drafting principle, from spec section 19: being explicit about what is
 * not yet defined is more protective than inventing precision that does not
 * exist. Nothing below claims a launch date, a headcount, or a capability
 * that has not been built.
 */

import { FOUNDING_OFFER } from '../../shared/content/foundingOffer';

export interface LegalSection {
  heading: string;
  paragraphs: readonly string[];
  /** Optional bulleted list rendered after the paragraphs. */
  list?: readonly string[];
}

export interface LegalPageContent {
  /** Route this page renders at. */
  path: string;
  title: string;
  /** Flip to true only when real copy is supplied below. */
  supplied: boolean;
  /** Shown under the title. */
  updated?: string;
  /** Intro paragraphs rendered before the sections. */
  body?: readonly string[];
  sections?: readonly LegalSection[];
  /** What the author has to cover. Not the copy itself. */
  requirements: readonly string[];
}

const LAST_UPDATED = 'Last updated August 16, 2026.';

const contactLine =
  'For any request or question about this page, use the contact email or mailing address shown in the footer of every page on this site.';

const privacySections: readonly LegalSection[] = [
  {
    heading: 'Who we are',
    paragraphs: [
      'My Herd Book is an early access signup site for a network being built to connect horse owners with equine service providers, starting in Maryland. This notice covers what this site collects and what we do with it.',
      contactLine,
    ],
  },
  {
    heading: 'What we collect',
    paragraphs: ['We collect only what the forms on this site ask for:'],
    list: [
      'Horse owner signup: your email address, your ZIP code, and optionally your first name.',
      'Provider signup: your name, business name, email address, ZIP code, the services you offer, and optionally a phone number, website, and notes you choose to add.',
      'Referrals: whatever you choose to tell us about a professional you recommend — their name, and optionally their category, location, contact details, and a note.',
      'How you arrived: the page you landed on, the site that referred you, and any campaign tags in the link you followed, so we can tell which outreach is working. This is tied to a random session identifier, not to an advertising profile.',
      'Basic usage events, such as a form being started or completed, tied to the same random session identifier.',
      'Ordinary server logs (page requested, response status, timing). Our logs do not record what you typed into forms.',
    ],
  },
  {
    heading: 'What we do not collect',
    paragraphs: [
      'The public site sets no cookies and uses no advertising trackers, no cross-site analytics, and no fingerprinting. We derive your county from the ZIP code you give us; we do not look up your location any other way.',
    ],
  },
  {
    heading: 'Why we collect it',
    paragraphs: [
      'To reserve your place on the early access list and confirm it by email. To contact you about the network — including, for providers, the invitation to claim your profile when the network opens. To plan the launch: signups by ZIP code, county, and service category tell us where demand and supply actually are. And to measure which outreach worked, so effort goes where it helps.',
      'We do not ask for information because it might be useful someday. If a field is on a form, it serves one of the purposes above.',
    ],
  },
  {
    heading: 'Referrals',
    paragraphs: [
      'When you recommend a professional, we store what you shared and may use it to reach out to that person or business ourselves, individually. We do not send them automated messages, and we do not attach your name to that outreach unless you have told us we may.',
    ],
  },
  {
    heading: 'Who else processes your information',
    paragraphs: [
      'Three service providers process data solely to run this site: Railway (hosting), Neon (database), and Resend (email delivery). Each receives only what it needs to do its job.',
      'We do not sell or rent personal information, and we do not share it with anyone for advertising.',
    ],
  },
  {
    heading: 'Email and unsubscribing',
    paragraphs: [
      'We send a confirmation when you sign up, and email about the network after that. Every email we send includes an unsubscribe link and our mailing address. Unsubscribing adds your address to a suppression list and we stop emailing it.',
    ],
  },
  {
    heading: 'Retention and your choices',
    paragraphs: [
      'We keep signup records while the early access program and the network it feeds are active. If you want your information suppressed or deleted, ask using the contact address in the footer and we will do it.',
    ],
  },
  {
    heading: 'Children',
    paragraphs: [
      'This site is for adults arranging services for horses. It is not directed to children, and we do not knowingly collect information from them.',
    ],
  },
  {
    heading: 'Changes to this notice',
    paragraphs: [
      'If this notice changes, this page and its date change with it. We will not quietly repurpose information collected under this notice for something it does not describe.',
    ],
  },
];

/**
 * The steps, flattened into prose for a numbered terms document. Joined by a
 * period rather than a dash so this page reads the same as the providers page
 * and both provider emails (`brand-voice.md`, punctuation). The legal prose
 * around it is owner-supplied and is not touched by a voice pass.
 */
const foundingSteps = FOUNDING_OFFER.steps.map(
  (step) => `${step.label}. ${step.title}. ${step.body}`,
);

const termsSections: readonly LegalSection[] = [
  {
    heading: 'What this site is',
    paragraphs: [
      'This site collects early access signups for a network being built to connect horse owners with equine service providers, starting in Maryland. It is not that network. Nothing is bought or sold here, joining is free, and signing up creates no obligation for you.',
      'We are building toward a Maryland launch. We do not promise a launch date, and the product as eventually launched may differ from what is described while it is being built.',
    ],
  },
  {
    heading: 'The Founding Provider program',
    paragraphs: [
      'This section governs the Founding Provider program. The offer, in the same words used everywhere on this site and in our email:',
      `“${FOUNDING_OFFER.statement}”`,
      'What the benefit covers:',
    ],
    list: FOUNDING_OFFER.benefitCovers,
  },
  {
    heading: 'How the program works',
    paragraphs: [
      'The program is deliberately two steps, and it matters which step you are on:',
      ...foundingSteps,
      'Submitting the provider signup form records founding eligibility. Eligibility is a reservation, not the benefit itself. The benefit — Founding Provider status and the upgraded profile with no recurring profile fee — is granted when you claim and complete your profile after the marketplace opens to horse owners, which is also when we verify the business is real. A reservation that is never claimed simply never converts; nothing further is owed on either side.',
      'The upgraded marketplace profile tier means the standard profile capabilities as they are defined at marketplace launch. We say “as defined at launch” because the marketplace does not exist yet, and we would rather be honest about that than invent detail now. Whatever the tier includes at launch, Founding Providers who claim their profile get it without a recurring profile fee.',
      'Future paid products and services — such as scheduling, payments, lead management, promotion, or other business tools — are separate from this benefit and are not included in it.',
      `${FOUNDING_OFFER.windowNote} The window may be extended; it will not be shortened. If the program terms materially change, changes will not reduce what an existing signup has already reserved.`,
    ],
  },
  {
    heading: 'Your submissions',
    paragraphs: [
      'You agree that what you submit is accurate and yours to share. Provider signups should describe a real business you are authorized to represent.',
      'When you refer a professional, share business contact information, for the purpose of us contacting them about the network — not private information about an individual.',
    ],
  },
  {
    heading: 'Acceptable use',
    paragraphs: [
      'Do not submit false signups, scrape the site, send automated traffic to its forms, or interfere with its operation.',
    ],
  },
  {
    heading: 'Disclaimers and limits',
    paragraphs: [
      'This is an early access site, provided as is. We may change, pause, or discontinue it. To the fullest extent permitted by law, we are not liable for indirect, incidental, or consequential damages arising from use of this site. Nothing in these terms limits liability that cannot lawfully be limited.',
    ],
  },
  {
    heading: 'Governing law',
    paragraphs: ['These terms are governed by the laws of the State of Maryland, USA.'],
  },
  {
    heading: 'Changes and contact',
    paragraphs: ['If these terms change, this page and its date change with it.', contactLine],
  },
];

const pageLiterals = {
  privacy: {
    path: '/privacy',
    title: 'Privacy',
    supplied: true,
    updated: LAST_UPDATED,
    body: [
      'This notice says what this site collects, why, and what happens to it — in plain language, because the people reading it are busy.',
    ],
    sections: privacySections,
    requirements: [
      'What is collected at signup, and why each field is collected.',
      'How signup information is stored, who can see it, and how long it is kept.',
      'Every third party that receives data: the host, the database, the email sender, the analytics tool.',
      'How someone asks to be removed, and the address that request goes to.',
      'The contact address for privacy questions.',
    ],
  },
  terms: {
    path: '/terms',
    title: 'Terms',
    supplied: true,
    updated: LAST_UPDATED,
    body: [
      'These are the terms for using this early access site and joining its signup lists. By signing up, you agree to them.',
    ],
    sections: termsSections,
    requirements: [
      'The ordinary site terms, adapted from a reputable generator and read end to end.',
      'A Founding Provider section written by hand — no generator knows about this program.',
      'That section must state the offer and what the benefit covers in the exact words of src/shared/content/foundingOffer.ts, which is what the provider confirmation email sends. If the terms are going to say something different, change that module first and let both follow it.',
      'That section must say what founding eligibility is, and that it is not yet the benefit itself.',
      'It must say the benefit is granted when the provider claims and completes their profile after the marketplace launches.',
      'It must say the upgraded profile tier is defined at marketplace launch, not now.',
      'It must say the early access window closes when the marketplace opens to horse owners.',
      'Site copy must never promise more than this page says. If they diverge, the copy is wrong.',
    ],
  },
} satisfies Record<string, LegalPageContent>;

export type LegalPageKey = keyof typeof pageLiterals;

const pages: Record<LegalPageKey, LegalPageContent> = pageLiterals;

export const legal = {
  /** Deliberately shouty. Renders only if a page's `supplied` flips back off. */
  banner: 'LEGAL COPY NOT YET SUPPLIED — DO NOT LAUNCH',
  bannerNote:
    'This page is a placeholder. No privacy notice or terms have been written, and the build agent is not permitted to write them.',
  requirementsHeading: 'What a human has to supply before this site goes public',
  sourceNote:
    'Supplied copy is dropped into src/client/content/legal.ts. The page layout does not change.',
  pages,
} as const;
